import React, { useCallback, useMemo } from "react";
import { SubscriptionWidgetProps } from "./types";
import BillingIcon from "assets/icons/billing.svg?react";
import { displayDate } from "helpers/time";
import { useTranslation } from "react-i18next";
import { differenceInCalendarDays } from "helpers/time";
import { useHasAccess } from "hooks/useHasAccess";
import { SilverSunnStripeSubscription } from "@switcherstudio/switcher-api-client";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { firstOrNull } from "utils/js-utils";
import { Widget } from "./Widget";
import { useHasPlanRole } from "hooks/useHasPlanRole";
import { usePauseStatus } from "hooks/usePauseStatus";
import { displayPlanName, isStudioPlanId } from "helpers/stripe";
import { getLocalizedDisplayString } from "helpers/localization";
import { useNavigate } from "react-router-dom";

export const SubscriptionWidget: React.FC<SubscriptionWidgetProps> = ({
    customer
}: SubscriptionWidgetProps) => {
    const { userInfo } = useSelector((state: RootState) => state.user);

    const navigate = useNavigate();

    const { i18n, t } = useTranslation();
    const isPaused = useHasAccess((userInfo) =>
        userInfo.Claims?.Details?.Roles.includes("Paused")
    );
    const isGrowUser = useHasPlanRole("Grow");

    const subscription = useMemo<SilverSunnStripeSubscription>(() => {
        return firstOrNull(customer?.StripeSubscriptions);
    }, [customer]);

    const {
        userWillPause,
        userIsPaused,
        pauseBeginDate,
        pauseEndDate,
        pausedPlanName
    } = usePauseStatus();

    const isTrial = useMemo<boolean>(() => {
        return (
            subscription?.PlanId === "Trial" ||
            userInfo?.Plan?.Details?.Status === "trialing"
        );
    }, [subscription, userInfo?.Plan?.Details?.Status]);

    const isCanceled = useMemo<boolean>(
        () => subscription?.CancelAtPeriodEnd || !!subscription?.CanceledAt,
        [subscription]
    );

    const planInfoText = useMemo<string>(() => {
        if (isPaused) {
            t("pause-copy:paused");
        } else if (userIsPaused) {
            return `${pausedPlanName} (${t("pause-copy:paused")})`;
        } else {
            return displayPlanName(
                getLocalizedDisplayString(
                    userInfo?.Plan?.Details?.DisplayName,
                    i18n.language
                ) ?? subscription?.PlanName
            );
        }
    }, [
        isPaused,
        pausedPlanName,
        userInfo?.Plan?.Details?.DisplayName,
        subscription?.PlanName,
        t,
        i18n.language,
        userIsPaused
    ]);

    const dateTitle = useMemo<string>(() => {
        if (isTrial) {
            return t("widgets:days-remaining");
        } else if (isCanceled) {
            return t("widgets:cancel-date");
        } else if (userWillPause) {
            return t("widgets:upcoming-pause");
        } else if (userIsPaused) {
            return t("widgets:reactivation-date");
        } else {
            return t("widgets:renewal-date");
        }
    }, [isCanceled, isTrial, t, userIsPaused, userWillPause]);

    const expireDate = useMemo<string>(() => {
        if (subscription) {
            if (isTrial) {
                const numDays =
                    differenceInCalendarDays(
                        new Date(subscription.ActiveUntil),
                        new Date(),
                        true
                    ) || 0;
                return `${numDays} ${
                    numDays === 1
                        ? t("widget:day-left")
                        : t("widgets:days-left")
                }`;
            } else {
                return displayDate(
                    subscription.ActiveUntil || "",
                    i18n.language
                );
            }
        } else {
            return `0 ${t("widgets:days-left")}`;
        }
    }, [i18n.language, isTrial, subscription, t]);

    const dateIntoText = useMemo<string>(() => {
        if (userWillPause) {
            return displayDate(
                pauseBeginDate,
                i18n.language,
                "dddd, MMMM D, YYYY"
            );
        } else if (userIsPaused) {
            return displayDate(
                pauseEndDate,
                i18n.language,
                "dddd, MMMM D, YYYY"
            );
        } else {
            return expireDate.toString();
        }
    }, [
        expireDate,
        i18n.language,
        pauseBeginDate,
        pauseEndDate,
        userIsPaused,
        userWillPause
    ]);

    const buttonText = useMemo((): "string" => {
        if (!subscription) {
            return t("subscription-page:select-plan");
        }

        if (isStudioPlanId(subscription.PlanId)) {
            return t("subscription-page:upgrade-studio-plan");
        }

        return t("subscription-page:manage-plan");
    }, [subscription, t]);

    const buttonVariant = useMemo(():
        | "btn-primary"
        | "btn-outline-secondary" => {
        if (!subscription) {
            return "btn-primary";
        }

        if (isStudioPlanId(subscription.PlanId)) {
            return "btn-primary";
        }

        if (userIsPaused) {
            return "btn-primary";
        }

        return "btn-outline-secondary";
    }, [subscription, userIsPaused]);

    const handleButtonClick = useCallback(() => {
        navigate("/subscription#openModal");
    }, [navigate]);

    return (
        <Widget
            icon={<BillingIcon />}
            title={t("widgets:subscription").toUpperCase()}
            details={[
                {
                    title: t("widgets:your-plan"),
                    infoText: planInfoText
                },
                {
                    title: dateTitle,
                    infoText: dateIntoText
                }
            ]}
            footerSubtitle={
                isPaused
                    ? t("pause-copy:no-renewal-message")
                    : !isTrial
                        ? t("widgets:cancel-message")
                        : !isGrowUser
                            ? t("widgets:watermark-removal")
                            : ""
            }
            button={
                <button
                    className={`btn ${buttonVariant}`}
                    onClick={handleButtonClick}
                >
                    {buttonText}
                </button>
            }
        />
    );
};
